import type {components} from '../contracts';
import type {RemoteCaptureOwner} from './capture';
type S=components['schemas'];
/** Selected-profile owner composed with the typed HTTP bridge and actual capture owner. The capture's original deadline is never reset. */
export class RemoteRuntimeProducer {
 private flight?:Promise<S['RuntimeMediaReceipt']>;
 private abort?:AbortController;
 private retired=false;
 private sequence=0;
 private timer?:ReturnType<typeof setTimeout>;
 private nextAt=0;
 private ready?:{sessionId:string;revision:number;binding:string;deadline:number;current():boolean};
 constructor(private deps:{enabled:boolean;current():boolean;scope?():{sessionId:string;revision:number};binding():Pick<S['CausalRuntimeMediaEvidenceRequest'],'epoch'|'transitionId'|'producerId'>;capture:Pick<RemoteCaptureOwner,'publish'>;reactions():Promise<S['CausalRuntimeMediaEvidenceRequest']['reactions']>;submit(request:S['CausalRuntimeMediaEvidenceRequest'],signal:AbortSignal):Promise<S['RuntimeMediaReceipt']>}){}
 sync(){
  clearTimeout(this.timer);
  if(this.retired||!this.deps.enabled||!this.deps.current()){this.ready=undefined;this.abort?.abort();return;}
  if(this.flight)return;
  const delay=Math.max(0,this.nextAt-performance.now());
  if(delay){this.timer=setTimeout(()=>this.sync(),delay);return;}
  void this.produce(new AbortController().signal).catch(()=>{this.ready=undefined;}).finally(()=>{if(!this.retired)this.timer=setTimeout(()=>this.sync(),Math.max(0,this.nextAt-performance.now()));});
 }
 async waitReady(session:{sessionId:string;revision:number},signal:AbortSignal){
  const started=performance.now(),binding=JSON.stringify(this.deps.binding());this.sync();
  await new Promise<void>((resolve,reject)=>{
   const poll=()=>{
    const now=performance.now(),scope=this.deps.scope?.(),ready=this.ready;
    if(this.retired||signal.aborted||!this.deps.current()||now<started||now-started>=3000||JSON.stringify(this.deps.binding())!==binding||scope?.sessionId!==session.sessionId||scope.revision!==session.revision){reject(Error('creator_program_unavailable'));return;}
    if(ready&&ready.sessionId===session.sessionId&&ready.revision===session.revision&&ready.binding===binding&&now<ready.deadline&&ready.current()){resolve();return;}
    setTimeout(poll,16);
   };poll();
  });
 }
 produce(signal:AbortSignal):Promise<S['RuntimeMediaReceipt']>{
  if(!this.deps.enabled||this.retired||this.flight||signal.aborted||!this.deps.current())return Promise.reject(Error('remote_runtime_unavailable'));
  const started=performance.now(),abort=new AbortController();this.abort=abort;const timer=setTimeout(()=>abort.abort(),999);const cancel=()=>abort.abort();signal.addEventListener('abort',cancel,{once:true});
  const binding=structuredClone(this.deps.binding());
  const current=()=>!this.retired&&!abort.signal.aborted&&performance.now()>=started&&performance.now()-started<999&&this.deps.current()&&Object.entries(binding).every(([k,v])=>this.deps.binding()[k as keyof typeof binding]===v);
  const work=(async()=>{
   try {
    const capture=await this.deps.capture.publish(abort.signal);
    if(!current()||!capture.current())throw Error('remote_runtime_unavailable');
    const reactions=await this.deps.reactions();
    if(!current()||!capture.current())throw Error('remote_runtime_unavailable');
    const request:S['CausalRuntimeMediaEvidenceRequest']={...binding,profile:'monotonic-causal-v1',challengeId:capture.challengeId,receiptId:capture.receiptId,bindingSha256:capture.bindingSha256,sequence:++this.sequence,program:capture.program,reactions};
    const submitted=performance.now(),receipt=await this.deps.submit(request,abort.signal);
    if(!current()||!capture.current()||receipt.program!=='verified'||!receipt.evidenceId||!Number.isSafeInteger(receipt.expiresAtMs)||receipt.expiresAtMs<=Date.now())throw Error('remote_runtime_unavailable');
    const scope=this.deps.scope?.();
    if(scope)this.ready={...scope,binding:JSON.stringify(binding),deadline:Math.min(started+999,submitted+receipt.expiresAtMs-Date.now()),current:capture.current};
    return receipt;
   // Space challenges from actual settlement: variable capture preparation must not
   // create a third still-live challenge inside the bounded two-challenge window.
   }finally{this.nextAt=performance.now()+500;clearTimeout(timer);signal.removeEventListener('abort',cancel);if(this.abort===abort)this.abort=undefined;}
  })();
  this.flight=work;void work.finally(()=>{if(this.flight===work)this.flight=undefined;}).catch(()=>undefined);return work;
 }
 dispose(){void this.close();}
 async close(){this.retired=true;this.ready=undefined;clearTimeout(this.timer);this.abort?.abort();await this.flight?.catch(()=>undefined);}
}
